import React, { FC, useState } from 'react';

import {
  Stack,
  HStack,
  Text,
  Box,
  OrderedList,
  ListItem,
} from '@chakra-ui/react';
import map from 'lodash/map';

import { HelpCount } from 'components/DataDisplay/Utils/HelpCount';
import { CopyIcon } from 'components/Icons';
import { renderCopyIcon } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/OnboardGcp/utils';
import { CommandBox } from 'containers/Setup/CloudAccounts/CloudAccountWizard/Components/utils/CommandBox';

interface Props {
  subscriptionId: string;
}

export const AutomaticallyPrepAzureSubscriptionForOnboarding: FC<Props> = props => {
  const [copied, setCopied] = useState('');
  const command =
    'curl -sL https://sm-test-434.s3.us-east-1.amazonaws.com/azure_onboarding.sh | bash -s -- --scope ' +
    props.subscriptionId;

  const items = [
    {
      label: 'Application Registration',
      description:
        'Creates an Azure AD application registration with the necessary API permissions to access Microsoft Graph data.',
    },
    {
      label: 'Service Principal Creation',
      description:
        'Creates a service principal for the application to enable non-interactive authentication.',
    },
    {
      label: 'Admin Consent',
      description:
        'Grants admin consent for the required Microsoft Graph ' +
        'permissions (User, Group, Application, Directory, Domain, Role Management, Policy, ' +
        'and User Authentication Method read access).',
    },
    {
      label: 'Custom Role Definition',
      description:
        'Creates a custom Azure RBAC role specifically for scanning web apps, with permissions ' +
        'to list configuration settings and host keys.',
    },
    {
      label: 'Role Assignments',
      description: 'Assigns three key roles to the service principal:',
      list: (
        <OrderedList spacing={2} pt={2}>
          <ListItem pl={4}>
            Reader role for general resource visibility
          </ListItem>
          <ListItem pl={4}>
            Key Vault Reader role for access to key vault data
          </ListItem>
          <ListItem pl={4}>
            The custom role created for web app scanning capabilities
          </ListItem>
        </OrderedList>
      ),
    },
    {
      label: 'Credential Management',
      description:
        'Generates and securely outputs client credentials (tenant ID, client ' +
        'ID, and client secret). You’ll need to paste these values in Ariksa UI for onboarding',
    },
  ];

  return (
    <Stack px={2} spacing={6} h="full">
      <Stack spacing={3}>
        <HStack align={'start'}>
          <HelpCount count={1} />
          <HStack>
            <Text>Execute this script from Azure CLI</Text>
            <Box color="primary">
              {renderCopyIcon(
                {
                  copyText: command,
                  icon: <CopyIcon p={0.5} color="primary" />,
                  tooltip: 'Copy command',
                },
                copied,
                setCopied,
              )}
            </Box>
          </HStack>
        </HStack>
        <Box pl={8}>
          <CommandBox>{command}</CommandBox>
        </Box>
      </Stack>
      <Stack pl={8} spacing={4}>
        <Text>The script performs the following steps:</Text>
        <OrderedList spacing={4}>
          {map(items, o => (
            <ListItem pl={4}>
              <Text>
                <Text as="b">{o.label}: </Text>
                {o.description}
              </Text>
              <Box pl={6}>{o?.list}</Box>
            </ListItem>
          ))}
        </OrderedList>
      </Stack>
    </Stack>
  );
};
